import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { GlassType } from '@/types/glassTypes';
import { useEstimate } from '@/contexts/EstimateContext';
import { toast } from 'sonner';
import AluminumSpacerColorModal from './AluminumSpacerColorModal';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Info } from 'lucide-react';
import SSGWarningModal from '@/components/modals/SSGWarningModal';

interface GlassTypeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  glassType: GlassType | null;
}

// Define the toggle options types
type MeasurementType = 'DLO' | 'Glass Size';
type FrameType = 'Storefront' | 'Curtain Wall';
type CurtainWallType = 'Regular' | 'SSG';

const GlassTypeModal: React.FC<GlassTypeModalProps> = ({
  open,
  onOpenChange,
  glassType,
}) => {
  const { addEstimateItem } = useEstimate();

  // State for toggle controls
  const [measurementType, setMeasurementType] = useState<MeasurementType>('DLO');
  const [frameType, setFrameType] = useState<FrameType>('Storefront');
  const [curtainWallType, setCurtainWallType] = useState<CurtainWallType>('Regular');

  // State for input fields
  const [width, setWidth] = useState<string>('');
  const [widthFraction, setWidthFraction] = useState<string>('0');
  const [height, setHeight] = useState<string>('');
  const [heightFraction, setHeightFraction] = useState<string>('0');
  const [quantity, setQuantity] = useState<string>('1');

  // State for insulated glass options
  const [hasArgonGas, setHasArgonGas] = useState<boolean>(false);
  const [hasAluminumSpacer, setHasAluminumSpacer] = useState<boolean>(false);
  const [aluminumSpacerColor, setAluminumSpacerColor] = useState<'Black' | 'Bronze' | 'Mill'>('Black');
  const [colorModalOpen, setColorModalOpen] = useState<boolean>(false);

  // State for SSG warning modal
  const [showSSGWarning, setShowSSGWarning] = useState<boolean>(false);

  // State for DLO addition toggle (for 1/4" glass in Storefront systems)
  const [dloAddition, setDloAddition] = useState<'1/2' | '5/8'>('1/2');

  // Define fraction options in 1/16" increments
  const fractionOptions = [
    { value: '0', label: '0' },
    { value: '1/16', label: '1/16' },
    { value: '1/8', label: '1/8' },
    { value: '3/16', label: '3/16' },
    { value: '1/4', label: '1/4' },
    { value: '5/16', label: '5/16' },
    { value: '3/8', label: '3/8' },
    { value: '7/16', label: '7/16' },
    { value: '1/2', label: '1/2' },
    { value: '9/16', label: '9/16' },
    { value: '5/8', label: '5/8' },
    { value: '11/16', label: '11/16' },
    { value: '3/4', label: '3/4' },
    { value: '13/16', label: '13/16' },
    { value: '7/8', label: '7/8' },
    { value: '15/16', label: '15/16' },
  ];

  // Helper function to convert fraction to decimal
  const fractionToDecimal = (fraction: string): number => {
    if (fraction === '0') return 0;
    const [numerator, denominator] = fraction.split('/');
    return parseFloat(numerator) / parseFloat(denominator);
  };

  // Helper function to check if glass thickness is 1/4"
  const isQuarterInchGlass = (): boolean => {
    if (!glassType) return false;

    return (
      glassType.thickness === 0.25 ||
      glassType.thicknessStr === '1/4' ||
      glassType.thicknessStr === '1/4"' ||
      (typeof glassType.thickness === 'string' &&
        (glassType.thickness === '1/4' || glassType.thickness === '1/4"'))
    );
  };

  // Helper function to check if glass thickness is 1/8", 5/32", or 3/16"
  const isThinGlass = (): boolean => {
    if (!glassType) return false;

    // Check for 1/8" thickness
    const is1_8 =
      glassType.thickness === 0.125 ||
      glassType.thicknessStr === '1/8' ||
      glassType.thicknessStr === '1/8"' ||
      (typeof glassType.thickness === 'string' &&
        (glassType.thickness === '1/8' || glassType.thickness === '1/8"'));

    // Check for 5/32" thickness
    const is5_32 =
      glassType.thickness === 0.15625 ||
      glassType.thicknessStr === '5/32' ||
      glassType.thicknessStr === '5/32"' ||
      (typeof glassType.thickness === 'string' &&
        (glassType.thickness === '5/32' || glassType.thickness === '5/32"'));

    // Check for 3/16" thickness
    const is3_16 =
      glassType.thickness === 0.1875 ||
      glassType.thicknessStr === '3/16' ||
      glassType.thicknessStr === '3/16"' ||
      (typeof glassType.thickness === 'string' &&
        (glassType.thickness === '3/16' || glassType.thickness === '3/16"'));

    return is1_8 || is5_32 || is3_16;
  };

  // Helper function to check if glass thickness is 3/8" or 1/2"
  const isMediumGlass = (): boolean => {
    if (!glassType) return false;

    // Check for 3/8" thickness
    const is3_8 =
      glassType.thickness === 0.375 ||
      glassType.thicknessStr === '3/8' ||
      glassType.thicknessStr === '3/8"' ||
      (typeof glassType.thickness === 'string' &&
        (glassType.thickness === '3/8' || glassType.thickness === '3/8"'));

    // Check for 1/2" thickness
    const is1_2 =
      glassType.thickness === 0.5 ||
      glassType.thicknessStr === '1/2' ||
      glassType.thicknessStr === '1/2"' ||
      (typeof glassType.thickness === 'string' &&
        (glassType.thickness === '1/2' || glassType.thickness === '1/2"'));

    return is3_8 || is1_2;
  };

  // Helper function to check if glass is a mirror type
  const isMirrorGlass = (): boolean => {
    if (!glassType) return false;

    // Check if type or name contains 'mirror'
    const normalizedType = (glassType.type || '').toLowerCase();
    const normalizedName = (glassType.name || '').toLowerCase();

    return normalizedType.includes('mirror') || normalizedName.includes('mirror');
  };

  // Helper function to check if glass is a fire rated type
  const isFireRatedGlass = (): boolean => {
    if (!glassType) return false;

    // Check if type or name contains 'fire'
    const normalizedType = (glassType.type || '').toLowerCase();
    const normalizedName = (glassType.name || '').toLowerCase();

    return normalizedType.includes('fire') || normalizedName.includes('fire');
  };

  // Helper function to check if glass is a pattern type
  const isPatternGlass = (): boolean => {
    if (!glassType) return false;

    // Check if type or name contains 'pattern'
    const normalizedType = (glassType.type || '').toLowerCase();
    const normalizedName = (glassType.name || '').toLowerCase();

    return normalizedType.includes('pattern') || normalizedName.includes('pattern');
  };

  // Helper function to check if glass is an IG Residential type
  const isIGResidentialGlass = (): boolean => {
    if (!glassType) return false;

    // Check if type contains 'ig residential'
    const normalizedType = (glassType.type || '').toLowerCase();

    return normalizedType.includes('ig residential');
  };

  // Helper function to check if glass is an IG Commercial type
  const isIGCommercialGlass = (): boolean => {
    if (!glassType) return false;

    // Check if type contains 'ig commercial'
    const normalizedType = (glassType.type || '').toLowerCase();

    return normalizedType.includes('ig commercial');
  };

  // Helper function to check if glass is any insulated glass unit (IG)
  const isInsulatedGlass = (): boolean => {
    return isIGResidentialGlass() || isIGCommercialGlass();
  };

  // Helper function to check if glass is 9/16" thick
  const isNineSixteenthInchGlass = (): boolean => {
    if (!glassType) return false;

    // Check for 9/16" thickness
    return (
      glassType.thickness === 0.5625 ||
      glassType.thicknessStr === '9/16' ||
      glassType.thicknessStr === '9/16"' ||
      (typeof glassType.thickness === 'string' &&
        (glassType.thickness === '9/16' || glassType.thickness === '9/16"'))
    );
  };

  // Reset states to defaults when opening the modal with a new glass type
  useEffect(() => {
    if (open && glassType) {
      // For medium glass (3/8" or 1/2"), mirror glass, or fire rated glass, only Glass Size is allowed
      if (isMediumGlass() || isMirrorGlass() || isFireRatedGlass()) {
        setMeasurementType('Glass Size');
      } else {
        // For other glass types, set default measurement type to DLO
        setMeasurementType('DLO');
      }

      // Set default frame type to Storefront
      setFrameType('Storefront');

      // Set default curtain wall type to Regular
      setCurtainWallType('Regular');

      // Reset input fields
      setWidth('');
      setWidthFraction('0');
      setHeight('');
      setHeightFraction('0');
      setQuantity('1');

      // Reset insulated glass options
      setHasArgonGas(false);
      setHasAluminumSpacer(false);
      setAluminumSpacerColor('Black');
      setColorModalOpen(false);
    }
  }, [open, glassType]);

  // Handle toggle changes
  const handleMeasurementTypeChange = (value: string) => {
    if (value) {
      // For medium glass (3/8" or 1/2"), mirror glass, or fire rated glass, only allow Glass Size
      if ((isMediumGlass() || isMirrorGlass() || isFireRatedGlass()) && value !== 'Glass Size') {
        return;
      }

      // Show warning if changing to DLO and system is already SSG
      if (value === 'DLO' && frameType === 'Curtain Wall' && curtainWallType === 'SSG') {
        setShowSSGWarning(true);
      }

      setMeasurementType(value as MeasurementType);
    }
  };

  const handleFrameTypeChange = (value: string) => {
    if (value) {
      // Don't allow frame type changes for thin, medium, mirror, fire rated, pattern, IG Residential, or 9/16" thick glass
      if (isThinGlass() || isMediumGlass() || isMirrorGlass() || isFireRatedGlass() || isPatternGlass() || isIGResidentialGlass() || isNineSixteenthInchGlass()) {
        return;
      }

      setFrameType(value as FrameType);

      // If switching to Curtain Wall and glass is 1/4", ensure Regular is selected (not SSG)
      if (value === 'Curtain Wall' && isQuarterInchGlass()) {
        setCurtainWallType('Regular');
      }
    }
  };

  const handleCurtainWallTypeChange = (value: string) => {
    if (value) {
      // Prevent selecting any curtain wall type for thin, medium, mirror, fire rated, pattern, IG Residential, or 9/16" thick glass
      if (isThinGlass() || isMediumGlass() || isMirrorGlass() || isFireRatedGlass() || isPatternGlass() || isIGResidentialGlass() || isNineSixteenthInchGlass()) {
        return;
      }

      // Prevent selecting SSG for 1/4" glass
      if (value === 'SSG' && isQuarterInchGlass()) {
        return;
      }

      // Show warning if SSG is selected and measurement type is DLO
      if (value === 'SSG' && measurementType === 'DLO') {
        setShowSSGWarning(true);
      }

      setCurtainWallType(value as CurtainWallType);
    }
  };

  // Handler for DLO addition toggle
  const handleDloAdditionChange = (value: string) => {
    if (value === '1/2' || value === '5/8') {
      setDloAddition(value as '1/2' | '5/8');
    }
  };

  // Handle adding item to estimate
  const handleAddToEstimate = () => {
    if (!glassType) {
      toast.error("No glass type selected");
      return;
    }

    // Validate width
    if (!width || isNaN(parseFloat(width)) || parseFloat(width) < 0) {
      toast.error("Please enter a valid width");
      return;
    }

    // Validate height
    if (!height || isNaN(parseFloat(height)) || parseFloat(height) < 0) {
      toast.error("Please enter a valid height");
      return;
    }

    // Calculate total width and height including fractions
    const totalWidth = parseFloat(width) + fractionToDecimal(widthFraction);
    const totalHeight = parseFloat(height) + fractionToDecimal(heightFraction);

    // Validate total dimensions
    if (totalWidth <= 0) {
      toast.error("Width must be greater than zero");
      return;
    }

    if (totalHeight <= 0) {
      toast.error("Height must be greater than zero");
      return;
    }

    // Validate quantity
    if (!quantity || isNaN(parseInt(quantity)) || parseInt(quantity) <= 0) {
      toast.error("Please enter a valid quantity");
      return;
    }

    // Prepare notes based on selected options
    const notes: string[] = [];

    // Add notes for insulated glass options if applicable
    if (isInsulatedGlass()) {
      if (hasArgonGas) {
        notes.push('Argon Gas');
      }

      if (hasAluminumSpacer) {
        notes.push(`${aluminumSpacerColor} Spacer`);
      }
    }

    // Add item to estimate
    addEstimateItem({
      glassType,
      width: totalWidth,
      height: totalHeight,
      quantity: parseInt(quantity),
      measurementType,
      frameType,
      curtainWallType: frameType === 'Curtain Wall' ? curtainWallType : undefined,
      // Include DLO addition option for 1/4" glass in Storefront systems
      storefrontDLOAddition: (isQuarterInchGlass() && frameType === 'Storefront' && measurementType === 'DLO') ? dloAddition : undefined,
      // Include insulated glass options if applicable
      hasArgonGas: isInsulatedGlass() ? hasArgonGas : undefined,
      hasAluminumSpacer: isInsulatedGlass() ? hasAluminumSpacer : undefined,
      aluminumSpacerColor: (isInsulatedGlass() && hasAluminumSpacer) ? aluminumSpacerColor : undefined,
      notes: notes.length > 0 ? notes : undefined,
    });

    // Show success message
    toast.success(`Added ${glassType.name} to estimate`);

    // Close modal
    onOpenChange(false);
  };

  if (!glassType) {
    return null;
  }

  return (
    <>
      {/* SSG Warning Modal */}
      <SSGWarningModal
        isOpen={showSSGWarning}
        onClose={() => setShowSSGWarning(false)}
      />

      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-[450px] bg-[#0f172a] text-white border-none">
        <DialogHeader>
          <DialogTitle className="text-[#60a5fa] text-xl">
            {glassType.name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          <div className="space-y-4">
            {/* Measurement Type Toggle */}
            <div>
              <Label
                className={`text-white mb-2 block ${isMediumGlass() || isMirrorGlass() || isFireRatedGlass() ? 'opacity-50' : ''}`}
              >
                Measurement Type
              </Label>
              <ToggleGroup
                type="single"
                value={isMediumGlass() || isMirrorGlass() || isFireRatedGlass() ? 'Glass Size' : measurementType}
                onValueChange={handleMeasurementTypeChange}
                className="w-full grid grid-cols-2 gap-0"
              >
                <ToggleGroupItem
                  value="DLO"
                  className="h-9 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground rounded-l-md rounded-r-none disabled:opacity-50 disabled:cursor-not-allowed disabled:data-[state=on]:bg-transparent disabled:data-[state=on]:text-muted-foreground disabled:data-[state=on]:border-muted"
                  disabled={isMediumGlass() || isMirrorGlass() || isFireRatedGlass()}
                >
                  DLO
                </ToggleGroupItem>
                <ToggleGroupItem
                  value="Glass Size"
                  className="h-9 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground rounded-r-md rounded-l-none disabled:opacity-50 disabled:cursor-not-allowed disabled:data-[state=on]:bg-transparent disabled:data-[state=on]:text-muted-foreground disabled:data-[state=on]:border-muted"
                >
                  Glass Size
                </ToggleGroupItem>
              </ToggleGroup>
            </div>

            {/* Frame Type Toggle - Disabled for thin, medium, mirror, fire rated, pattern, IG Residential, or 9/16" thick glass */}
            <div>
              <Label
                className={`text-white mb-2 block ${isThinGlass() || isMediumGlass() || isMirrorGlass() || isFireRatedGlass() || isPatternGlass() || isIGResidentialGlass() || isNineSixteenthInchGlass() ? 'opacity-50' : ''}`}
              >
                Frame Type
              </Label>
              <ToggleGroup
                type="single"
                value={frameType}
                onValueChange={handleFrameTypeChange}
                className="w-full grid grid-cols-2 gap-0"
                disabled={isThinGlass() || isMediumGlass() || isMirrorGlass() || isFireRatedGlass() || isPatternGlass() || isIGResidentialGlass() || isNineSixteenthInchGlass()}
              >
                <ToggleGroupItem
                  value="Storefront"
                  className="h-9 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground rounded-l-md rounded-r-none disabled:opacity-50 disabled:cursor-not-allowed disabled:data-[state=on]:bg-transparent disabled:data-[state=on]:text-muted-foreground disabled:data-[state=on]:border-muted"
                  disabled={isThinGlass() || isMediumGlass() || isMirrorGlass() || isFireRatedGlass() || isPatternGlass() || isIGResidentialGlass() || isNineSixteenthInchGlass()}
                >
                  Storefront
                </ToggleGroupItem>
                <ToggleGroupItem
                  value="Curtain Wall"
                  className="h-9 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground rounded-r-md rounded-l-none disabled:opacity-50 disabled:cursor-not-allowed disabled:data-[state=on]:bg-transparent disabled:data-[state=on]:text-muted-foreground disabled:data-[state=on]:border-muted"
                  disabled={isThinGlass() || isMediumGlass() || isMirrorGlass() || isFireRatedGlass() || isPatternGlass() || isIGResidentialGlass() || isNineSixteenthInchGlass()}
                >
                  Curtain Wall
                </ToggleGroupItem>
              </ToggleGroup>
            </div>

            {/* Curtain Wall Type Toggle - Disabled for thin/medium/mirror/fire rated/pattern/IG Residential/9/16" thick glass or when Storefront is selected */}
            <div>
              <Label
                className={`text-white mb-2 block ${frameType === 'Storefront' || isThinGlass() || isMediumGlass() || isMirrorGlass() || isFireRatedGlass() || isPatternGlass() || isIGResidentialGlass() || isNineSixteenthInchGlass() ? 'opacity-50' : ''}`}
              >
                Curtain Wall Type
              </Label>
              <ToggleGroup
                type="single"
                value={curtainWallType}
                onValueChange={handleCurtainWallTypeChange}
                className="w-full grid grid-cols-2 gap-0"
                disabled={frameType === 'Storefront' || isThinGlass() || isMediumGlass() || isMirrorGlass() || isFireRatedGlass() || isPatternGlass() || isIGResidentialGlass() || isNineSixteenthInchGlass()}
              >
                <ToggleGroupItem
                  value="Regular"
                  className="h-9 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground rounded-l-md rounded-r-none disabled:opacity-50 disabled:cursor-not-allowed disabled:data-[state=on]:bg-transparent disabled:data-[state=on]:text-muted-foreground disabled:data-[state=on]:border-muted"
                  disabled={frameType === 'Storefront' || isThinGlass() || isMediumGlass() || isMirrorGlass() || isFireRatedGlass() || isPatternGlass() || isIGResidentialGlass() || isNineSixteenthInchGlass()}
                >
                  Regular
                </ToggleGroupItem>
                <ToggleGroupItem
                  value="SSG"
                  className="h-9 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground rounded-r-md rounded-l-none disabled:opacity-50 disabled:cursor-not-allowed disabled:data-[state=on]:bg-transparent disabled:data-[state=on]:text-muted-foreground disabled:data-[state=on]:border-muted"
                  disabled={frameType === 'Storefront' || isQuarterInchGlass() || isThinGlass() || isMediumGlass() || isMirrorGlass() || isFireRatedGlass() || isPatternGlass() || isIGResidentialGlass() || isNineSixteenthInchGlass()}
                >
                  SSG
                </ToggleGroupItem>
              </ToggleGroup>
            </div>
          </div>

          <div className="bg-white/5 p-4 rounded-md mt-4">
            <h3 className="text-[#60a5fa] font-medium mb-2">Adjustment Information</h3>
            <div className="text-sm">
              {measurementType === 'DLO' ? (
                <>
                  <p className="mb-2"><strong>Note:</strong> All adjustments are automatically calculated and included when DLO is selected.</p>

                  {/* For thin glass (1/8", 5/32", 3/16") */}
                  {isThinGlass() ? (
                    <p>Add 1/2" to both width and height</p>
                  ) : isPatternGlass() ? (
                    <p>Add 1/2" to both width and height</p>
                  ) : isIGResidentialGlass() || isNineSixteenthInchGlass() ? (
                    <p>Add 5/8" to both width and height</p>
                  ) : (
                    <>
                      {/* For medium glass (3/8", 1/2"), mirror glass, or fire rated glass - should never happen as DLO is disabled */}
                      {isMediumGlass() || isMirrorGlass() || isFireRatedGlass() ? (
                        <p>Glass Size measurements only - no adjustments needed</p>
                      ) : (
                        <>
                          {/* For 1/4" glass thickness */}
                          {isQuarterInchGlass() ? (
                            <>
                              {frameType === 'Storefront' ? (
                                <>
                                  <p className="mb-2">Add {dloAddition}" to both width and height</p>

                                  {/* DLO Addition Toggle for 1/4" glass in Storefront */}
                                  <div className="mt-3">
                                    <Label className="text-white text-xs mb-1 block">DLO Addition Size:</Label>
                                    <ToggleGroup
                                      type="single"
                                      value={dloAddition}
                                      onValueChange={handleDloAdditionChange}
                                      className="w-full grid grid-cols-2 gap-0"
                                    >
                                      <ToggleGroupItem
                                        value="1/2"
                                        className="h-7 text-sm data-[state=on]:bg-primary data-[state=on]:text-primary-foreground rounded-l-md rounded-r-none"
                                      >
                                        1/2"
                                      </ToggleGroupItem>
                                      <ToggleGroupItem
                                        value="5/8"
                                        className="h-7 text-sm data-[state=on]:bg-primary data-[state=on]:text-primary-foreground rounded-r-md rounded-l-none"
                                      >
                                        5/8"
                                      </ToggleGroupItem>
                                    </ToggleGroup>
                                  </div>
                                </>
                              ) : (
                                <>
                                  {curtainWallType === 'Regular' ? (
                                    <p>Add 3/4" to both width and height</p>
                                  ) : (
                                    <p>Add 1-1/2" to width and 1-1/4" to height</p>
                                  )}
                                </>
                              )}
                            </>
                          ) : (
                            <>
                              {/* For 1" glass thickness */}
                              {frameType === 'Storefront' ? (
                                <p>Add 3/4" to both width and height</p>
                              ) : (
                                <>
                                  {curtainWallType === 'Regular' ? (
                                    <p>Add 1" to both width and height</p>
                                  ) : (
                                    <p>Add 1-1/2" to width and 1-1/4" to height</p>
                                  )}
                                </>
                              )}
                            </>
                          )}
                        </>
                      )}
                    </>
                  )}
                </>
              ) : (
                <p>No adjustments needed for Glass Size measurements</p>
              )}
            </div>
          </div>

          {/* Dimensions and Quantity Inputs */}
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-white mb-2 block">Width (inches)</Label>
                <div className="flex space-x-2">
                  <div className="flex-[3]">
                    <Input
                      type="number"
                      min="0"
                      step="1"
                      value={width}
                      onChange={(e) => setWidth(e.target.value)}
                      className="bg-[#1e293b] text-white border-[#4b5563] w-full"
                      placeholder="Inches"
                    />
                  </div>
                  <div className="flex-1 max-w-[80px]">
                    <Select value={widthFraction} onValueChange={setWidthFraction}>
                      <SelectTrigger className="bg-[#1e293b] text-white border-[#4b5563] h-10 px-2">
                        <SelectValue placeholder="Fraction" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1e293b] text-white border-[#4b5563] min-w-[80px]">
                        {fractionOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              <div>
                <Label className="text-white mb-2 block">Height (inches)</Label>
                <div className="flex space-x-2">
                  <div className="flex-[3]">
                    <Input
                      type="number"
                      min="0"
                      step="1"
                      value={height}
                      onChange={(e) => setHeight(e.target.value)}
                      className="bg-[#1e293b] text-white border-[#4b5563] w-full"
                      placeholder="Inches"
                    />
                  </div>
                  <div className="flex-1 max-w-[80px]">
                    <Select value={heightFraction} onValueChange={setHeightFraction}>
                      <SelectTrigger className="bg-[#1e293b] text-white border-[#4b5563] h-10 px-2">
                        <SelectValue placeholder="Fraction" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1e293b] text-white border-[#4b5563] min-w-[80px]">
                        {fractionOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <Label className="text-white mb-2 block">Quantity</Label>
              <Input
                type="number"
                min="1"
                step="1"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                className="bg-[#1e293b] text-white border-[#4b5563] w-full"
                placeholder="Enter quantity"
              />
            </div>

            {/* Insulated Glass Options - Only show for IG Commercial and IG Residential */}
            {isInsulatedGlass() && (
              <div className="mt-4 pt-4 border-t border-[#4b5563]">
                <h3 className="text-[#60a5fa] font-medium mb-3">Insulated Glass Options</h3>

                {/* Argon Gas Option */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <Label htmlFor="argon-gas" className="cursor-pointer">Add Argon Gas</Label>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent className="bg-[#1e293b] text-white border-[#4b5563] max-w-xs">
                          <p>Adds $2.00 per square foot to the price</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <Switch
                    id="argon-gas"
                    checked={hasArgonGas}
                    onCheckedChange={setHasArgonGas}
                  />
                </div>

                {/* Aluminum Spacer Option */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Label htmlFor="aluminum-spacer" className="cursor-pointer">Aluminum Spacer</Label>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent className="bg-[#1e293b] text-white border-[#4b5563] max-w-xs">
                          <p>Adds $0.75 per square foot to the price</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <Switch
                    id="aluminum-spacer"
                    checked={hasAluminumSpacer}
                    onCheckedChange={(checked) => {
                      setHasAluminumSpacer(checked);
                      if (checked) {
                        setColorModalOpen(true);
                      }
                    }}
                  />
                </div>

                {/* Show selected color if aluminum spacer is enabled */}
                {hasAluminumSpacer && (
                  <div className="mt-2 text-sm text-muted-foreground flex justify-between items-center">
                    <span>Selected color: {aluminumSpacerColor}</span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setColorModalOpen(true)}
                      className="h-7 px-2 py-1 text-xs bg-transparent hover:bg-[#1e293b] text-white border-[#4b5563]"
                    >
                      Change
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Aluminum Spacer Color Modal */}
          <AluminumSpacerColorModal
            open={colorModalOpen}
            onOpenChange={setColorModalOpen}
            selectedColor={aluminumSpacerColor}
            onColorSelect={setAluminumSpacerColor}
          />
        </div>
        <DialogFooter className="mt-6">
          <Button
            onClick={handleAddToEstimate}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white"
          >
            Add to Estimate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default GlassTypeModal;
